import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpErrorResponse,
  HttpContextToken,
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  catchError,
  switchMap,
  Observable,
  throwError,
  filter,
  take,
  timeout,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthStore } from './state/auth.store';
import { REFRESH_WAIT_TIMEOUT_MS } from './utils/refresh-timeouts';

export const SKIP_AUTH = new HttpContextToken<boolean>(() => false);

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
) => {
  const authStore = inject(AuthStore);

  const isApiCall = req.url.startsWith(environment.apiUrl);
  if (!isApiCall) {
    return next(req);
  }

  const isAuthEndpoint =
    req.url.includes('/users/token') ||
    req.url.includes('/users/refresh-token');
  if (isAuthEndpoint) {
    return next(req);
  }

  if (req.context.get(SKIP_AUTH)) {
    return next(req);
  }

  const effectiveAccessToken$ = toObservable(
    authStore.effectiveAccessToken
  ).pipe(
    filter((token) => typeof token === 'string' && token.length > 0),
    take(1)
  );

  return effectiveAccessToken$.pipe(
    switchMap((accessToken) => {
      if (!accessToken) {
        return throwError(() => new Error('No access token available'));
      }

      return next(addTokenToRequest(req, accessToken)).pipe(
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return handleUnauthorizedError(req, next, authStore, accessToken);
          }

          if (error instanceof HttpErrorResponse && error.status === 403) {
            return throwError(() => new Error('Access denied'));
          }

          return throwError(() => error);
        })
      );
    })
  );
};

function addTokenToRequest(req: HttpRequest<any>, token: string) {
  return req.clone({
    headers: req.headers.set('Authorization', `Bearer ${token}`),
  });
}

function handleUnauthorizedError(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authStore: InstanceType<typeof AuthStore>,
  failedAccessToken: string
): Observable<HttpEvent<any>> {
  authStore.getRefreshToken(failedAccessToken);

  return authStore.$getRefreshTokenCallStatus.pipe(
    filter(
      (status) =>
        status === 'loaded' ||
        (typeof status === 'object' && status !== null && 'error' in status)
    ),
    take(1),
    timeout(REFRESH_WAIT_TIMEOUT_MS),
    switchMap((status) => {
      if (status !== 'loaded') {
        return throwError(
          () => new Error('Session expired or refresh failed.')
        );
      }

      const newAccessToken = authStore.effectiveAccessToken();
      if (!newAccessToken) {
        return throwError(
          () =>
            new Error('Refresh failed: No new token available post-refresh.')
        );
      }

      return next(addTokenToRequest(req, newAccessToken));
    })
  );
}
